/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getOptionDecoder,
  getOptionEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type Option,
  type OptionOrNullable,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const CREATE_CREDENTIAL_TEMPLATE_DISCRIMINATOR = new Uint8Array([
  183, 109, 12, 128, 134, 8, 139, 144,
]);

export function getCreateCredentialTemplateDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    CREATE_CREDENTIAL_TEMPLATE_DISCRIMINATOR,
  );
}

export type CreateCredentialTemplateInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountCredentialTemplate extends string | AccountMeta<string> = string,
  TAccountCredentialType extends string | AccountMeta<string> = string,
  TAccountIssuer extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountCredentialTemplate extends string
        ? WritableAccount<TAccountCredentialTemplate>
        : TAccountCredentialTemplate,
      TAccountCredentialType extends string
        ? ReadonlyAccount<TAccountCredentialType>
        : TAccountCredentialType,
      TAccountIssuer extends string
        ? WritableSignerAccount<TAccountIssuer> &
            AccountSignerMeta<TAccountIssuer>
        : TAccountIssuer,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type CreateCredentialTemplateInstructionData = {
  discriminator: ReadonlyUint8Array;
  name: string;
  imageUri: string;
  crossmintTemplateId: Option<string>;
};

export type CreateCredentialTemplateInstructionDataArgs = {
  name: string;
  imageUri: string;
  crossmintTemplateId: OptionOrNullable<string>;
};

export function getCreateCredentialTemplateInstructionDataEncoder(): Encoder<CreateCredentialTemplateInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["imageUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      [
        "crossmintTemplateId",
        getOptionEncoder(
          addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder()),
        ),
      ],
    ]),
    (value) => ({
      ...value,
      discriminator: CREATE_CREDENTIAL_TEMPLATE_DISCRIMINATOR,
    }),
  );
}

export function getCreateCredentialTemplateInstructionDataDecoder(): Decoder<CreateCredentialTemplateInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["imageUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    [
      "crossmintTemplateId",
      getOptionDecoder(addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())),
    ],
  ]);
}

export function getCreateCredentialTemplateInstructionDataCodec(): Codec<
  CreateCredentialTemplateInstructionDataArgs,
  CreateCredentialTemplateInstructionData
> {
  return combineCodec(
    getCreateCredentialTemplateInstructionDataEncoder(),
    getCreateCredentialTemplateInstructionDataDecoder(),
  );
}

export type CreateCredentialTemplateAsyncInput<
  TAccountCredentialTemplate extends string = string,
  TAccountCredentialType extends string = string,
  TAccountIssuer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  credentialTemplate?: Address<TAccountCredentialTemplate>;
  credentialType: Address<TAccountCredentialType>;
  issuer: TransactionSigner<TAccountIssuer>;
  systemProgram?: Address<TAccountSystemProgram>;
  name: CreateCredentialTemplateInstructionDataArgs["name"];
  imageUri: CreateCredentialTemplateInstructionDataArgs["imageUri"];
  crossmintTemplateId: CreateCredentialTemplateInstructionDataArgs["crossmintTemplateId"];
};

export async function getCreateCredentialTemplateInstructionAsync<
  TAccountCredentialTemplate extends string,
  TAccountCredentialType extends string,
  TAccountIssuer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateCredentialTemplateAsyncInput<
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountIssuer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  CreateCredentialTemplateInstruction<
    TProgramAddress,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountIssuer,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credentialTemplate: {
      value: input.credentialTemplate ?? null,
      isWritable: true,
    },
    credentialType: { value: input.credentialType ?? null, isWritable: false },
    issuer: { value: input.issuer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.credentialTemplate.value) {
    accounts.credentialTemplate.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            99, 114, 101, 100, 101, 110, 116, 105, 97, 108, 95, 116, 101, 109,
            112, 108, 97, 116, 101,
          ]),
        ),
        getAddressEncoder().encode(
          expectAddress(accounts.credentialType.value),
        ),
        getUtf8Encoder().encode(expectSome(args.name)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credentialTemplate),
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.issuer),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateCredentialTemplateInstructionDataEncoder().encode(
      args as CreateCredentialTemplateInstructionDataArgs,
    ),
    programAddress,
  } as CreateCredentialTemplateInstruction<
    TProgramAddress,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountIssuer,
    TAccountSystemProgram
  >);
}

export type CreateCredentialTemplateInput<
  TAccountCredentialTemplate extends string = string,
  TAccountCredentialType extends string = string,
  TAccountIssuer extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  credentialTemplate: Address<TAccountCredentialTemplate>;
  credentialType: Address<TAccountCredentialType>;
  issuer: TransactionSigner<TAccountIssuer>;
  systemProgram?: Address<TAccountSystemProgram>;
  name: CreateCredentialTemplateInstructionDataArgs["name"];
  imageUri: CreateCredentialTemplateInstructionDataArgs["imageUri"];
  crossmintTemplateId: CreateCredentialTemplateInstructionDataArgs["crossmintTemplateId"];
};

export function getCreateCredentialTemplateInstruction<
  TAccountCredentialTemplate extends string,
  TAccountCredentialType extends string,
  TAccountIssuer extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: CreateCredentialTemplateInput<
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountIssuer,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): CreateCredentialTemplateInstruction<
  TProgramAddress,
  TAccountCredentialTemplate,
  TAccountCredentialType,
  TAccountIssuer,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    credentialTemplate: {
      value: input.credentialTemplate ?? null,
      isWritable: true,
    },
    credentialType: { value: input.credentialType ?? null, isWritable: false },
    issuer: { value: input.issuer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.credentialTemplate),
      getAccountMeta(accounts.credentialType),
      getAccountMeta(accounts.issuer),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getCreateCredentialTemplateInstructionDataEncoder().encode(
      args as CreateCredentialTemplateInstructionDataArgs,
    ),
    programAddress,
  } as CreateCredentialTemplateInstruction<
    TProgramAddress,
    TAccountCredentialTemplate,
    TAccountCredentialType,
    TAccountIssuer,
    TAccountSystemProgram
  >);
}

export type ParsedCreateCredentialTemplateInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    credentialTemplate: TAccountMetas[0];
    credentialType: TAccountMetas[1];
    issuer: TAccountMetas[2];
    systemProgram: TAccountMetas[3];
  };
  data: CreateCredentialTemplateInstructionData;
};

export function parseCreateCredentialTemplateInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedCreateCredentialTemplateInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 4) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      credentialTemplate: getNextAccount(),
      credentialType: getNextAccount(),
      issuer: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getCreateCredentialTemplateInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
